import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Icon, { SettingFilled } from '@ant-design/icons';
import { Resizable, ResizeCallbackData } from 'react-resizable';
import { WidgetState, WidgetPortContext } from '@kemu-io/kemu-core/types';
import { WidgetPort } from '@kemu-io/hs-types';
import SwitchProcessor, { getDefaultState, type SwitchState, OutPorts } from '@kemu-io/kemu-core/widgets/switch/index.js';
import 'react-resizable/css/styles.css';
import { Button } from 'antd';
import classNames from 'classnames';
import { PortLocation } from '../../../types/canvas_t';
import { GetPortsInformationFunction, GateUI, GateUIProps, GateCustomSettingsProps } from '../index.ts';
import GateIcon from '../../gateIcon/gateIcon';
import styles from './switch.module.css';
import useTranslation from '@hooks/useTranslation';
import KemuSwitch from '@components/form-control/kemuSwitch/KemuSwitch';
import { ReactComponent as SwitchIcon } from '@src/assets/img/gates/switch.svg';
import TemplateEditor, { PrefixHighlight } from '@components/TemplateEditor/TemplateEditor';
import useReactiveWidgetState from '@common/hooks/useReactiveWidgetState';
import { SETTINGS_CONTAINER_CLASS } from '@common/constants';
import ResizeHandle from '@components/ResizeHandle/ResizeHandle';
import { useLogicMapperContext } from '@common/context/LogicMapperContext/LogicMapperContext.tsx';
import { removeWidgetPortConnections } from '@src/app/recipe/utils';

type Props = GateUIProps;

const DEFAULT_W = 350;
const DEFAULT_H = 120;

/**
 * Settings dialog for Switch widget.
 * Allows toggling multiple outputs and adjusting number of rows.
 */
const CustomSettingsDialog = (props: GateCustomSettingsProps): React.JSX.Element => {
  const { redrawPortsAndConnections, recipeId, blockId, gateInfo, recipeType } = props;
  const t = useTranslation('LogicMapper.Widgets.Switch.Settings');
  const [state, setState] = useReactiveWidgetState<SwitchState>(props.recipeId, props.blockId, props.gateInfo.id);
  const fixedState: SwitchState = { ...getDefaultState(), ...state };
  const { toggleWidgetPortsVisibility } = useLogicMapperContext();
  const previousRowCountRef = useRef<number>(fixedState.conditions?.length || 1);

  // Use local state to prevent cursor jumping - only sync to global on blur
  const [localRows, setLocalRows] = useState(() => fixedState.conditions || []);

  const size = useMemo(() => ({
    width: fixedState.size?.width || DEFAULT_W,
    height: fixedState.size?.height || DEFAULT_H,
  }), [fixedState.size]);

  const setRows = (count: number) => {
    const newCount = Math.max(1, Math.min(10, count));
    const previousCount = previousRowCountRef.current;
    const portRemoved = newCount < previousCount;

    if (portRemoved) {
      // Get the current output names
      const portContext: WidgetPortContext = {
        recipePoolId: recipeId,
        recipeType: recipeType,
        thingRecipeId: blockId,
        widgetId: gateInfo.id,
      };
      const outputNames = SwitchProcessor.getOutputNames(fixedState, portContext);

      // Remove connections for the ports that will be deleted
      for (let i = newCount; i < previousCount; i++) {
        const outputToRemove = outputNames[i];
        if (outputToRemove) {
          removeWidgetPortConnections(
            recipeId,
            blockId,
            gateInfo.id,
            outputToRemove.name,
          );
        }
      }
    }

    const next = Array.from({ length: newCount }, (_, i) => fixedState.conditions[i] || { expression: '', output: 'input' });
    setState({ ...fixedState, conditions: next });
    previousRowCountRef.current = newCount;
    props.repaintExistingPorts?.();
  };

  /**
   * Handles toggling between single and multiple output modes.
   * Removes connections from ports that will no longer exist after the mode change.
   */
  const handleMultipleOutputsToggle = (checked: boolean) => {
    const portContext: WidgetPortContext = {
      recipePoolId: recipeId,
      recipeType: recipeType,
      thingRecipeId: blockId,
      widgetId: gateInfo.id,
    };
    const outputNames = SwitchProcessor.getOutputNames(fixedState, portContext);

    if (checked) {
      // Switching from single output to multiple outputs
      // Remove connections from the 'out' port (it won't exist in multiple output mode)
      removeWidgetPortConnections(recipeId, blockId, gateInfo.id, OutPorts.out);
    } else {
      // Switching from multiple outputs to single output
      // Remove connections from all numbered ports (they won't exist in single output mode)
      // Keep 'else' port as it exists in both modes
      outputNames.forEach((port) => {
        if (port.name !== OutPorts.else) {
          removeWidgetPortConnections(recipeId, blockId, gateInfo.id, port.name);
        }
      });
    }

    setState({ ...fixedState, multipleOutputs: checked });
  };

  const handleResize = useCallback((event: React.SyntheticEvent<Element, Event>, data: ResizeCallbackData) => {
    setState(current => ({
      ...current,
      size: { width: data.size.width, height: data.size.height },
    }));
  }, [setState]);


  const updateRowLocal = useCallback((idx: number, field: 'expression' | 'output', value: string) => {
    const next = [...localRows];
    next[idx] = { ...next[idx], [field]: value };
    setLocalRows(next);
  }, [localRows]);

  const syncRowToGlobal = useCallback(() => {
    setState({ ...fixedState, conditions: localRows });
  }, [fixedState, localRows, setState]);

  const rows = localRows;


  // Sync local state when conditions change externally (e.g., Adding/Removing rows)
  useEffect(() => {
    setLocalRows(fixedState.conditions || []);
  }, [fixedState.conditions]);

  useEffect(() => {
    toggleWidgetPortsVisibility({ widgetId: props.gateInfo.id, visible: false });
    return () => {
      toggleWidgetPortsVisibility({ widgetId: props.gateInfo.id, visible: true });
      redrawPortsAndConnections();
    };
  }, []);

  return (
    <div className={classNames(styles.SettingsContainer, SETTINGS_CONTAINER_CLASS)}>
      <Resizable
        height={size.height}
        width={size.width}
        minConstraints={[DEFAULT_W, DEFAULT_H]}
        resizeHandles={['se']}
        onResize={handleResize}
        handle={<ResizeHandle />}
      >

        <div className={classNames(styles.InnerBody)}>
          {props.children}
          <div className={styles.ControlsRow}>
            <div className={styles.SwitchContainer}>
              <label>{t('MultipleOutputs', 'Multiple outputs')}</label>
              <KemuSwitch
                size="small"
                checked={fixedState.multipleOutputs}
                onChange={handleMultipleOutputsToggle}
              />
            </div>

            <div className={styles.RowsTools}>
              <label>{t('Rows', 'Rows')}</label>
              <div>
                <Button size="small" onClick={() => setRows((fixedState.conditions?.length || 1) - 1)}>-</Button>
                <span style={{ padding: '0 8px' }}>{fixedState.conditions?.length || 1}</span>
                <Button size="small" onClick={() => setRows((fixedState.conditions?.length || 1) + 1)}>+</Button>
              </div>
            </div>
          </div>


          <div className={`${styles.TableContainer}`} style={{ width: size.width, height: size.height }}>
            <div className={styles.TableHeader}>
              <div className={styles.ColExpression}>{t('Table.Expression', 'Expression')}</div>
              <div className={styles.ColOutput}>{t('Table.Output', 'Output')}</div>
            </div>
            <div className={styles.RowsContainer}>
              {rows.map((row, idx) => (
                <div className={styles.Row} key={idx}>
                  <TemplateEditor
                    value={row.expression || ''}
                    onChange={(v) => updateRowLocal(idx, 'expression', v)}
                    onBlur={syncRowToGlobal}
                    disabled={props.gateInfo.disabled}
                    className={styles.Editor}
                    padding={0}
                    highlightVariablesOutsideBraces={true}
                    customPrefixes={customPrefixes}
                  />
                  <TemplateEditor
                    value={row.output ?? 'input'}
                    onChange={(v) => updateRowLocal(idx, 'output', v)}
                    onBlur={syncRowToGlobal}
                    disabled={props.gateInfo.disabled}
                    className={styles.Editor}
                    padding={0}
                    highlightVariablesOutsideBraces={true}
                    customPrefixes={customPrefixes}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </Resizable>
    </div>
  );
};


const customPrefixes: PrefixHighlight[] = [
  { prefix: 'input', color: '#de6414',  allowBrackets: true, allowStandalone: true },
];

const SwitchWidget = (props: Props): React.JSX.Element => {
  const [state] = useReactiveWidgetState<SwitchState>(props.recipeId, props.thingRecipeId, props.info.id);
  const fixedState: SwitchState = { ...getDefaultState(), ...state };

  let bodyHeight = 40 + ((fixedState.conditions?.length || 1) - 1) * 14;

  if (!fixedState.multipleOutputs) {
    bodyHeight = 52;
  }

  return (
    <div className={styles.GateBody}>
      <div className={styles.InnerWidgetBody} style={{ height: bodyHeight }}>
        <span className={styles.SettingsBtn} onClick={props.openCustomSettings}>
          <SettingFilled />
        </span>
        <div className={styles.InnerBody}>
          <Icon component={SwitchIcon} className={styles.SwitchIcon} />
        </div>
      </div>
    </div>
  );
};

/** Icon to be added to the bar */
const GateBarIcon = (): React.JSX.Element => {
  return (
    <GateIcon icon={<Icon component={SwitchIcon} />} />
  );
};

const getPortsInformation: GetPortsInformationFunction = (state: WidgetState<SwitchState>, widgetInfo) => {
  const portContext: WidgetPortContext = {
    recipePoolId: widgetInfo.recipePoolId,
    recipeType: widgetInfo.recipeType,
    thingRecipeId: widgetInfo.thingRecipeId,
    widgetId: widgetInfo.id,
  };

  const outputNames = SwitchProcessor.getOutputNames(state, portContext);
  const inputNames = SwitchProcessor.getInputNames(state, portContext);

  const inputPositions: Record<string, PortLocation> = {
    input: [0, 0.5, -1, 0],
  };


  // Spread outputs evenly on the right; fallback to center for single output
  const outputPositions: Record<string, PortLocation> = {};
  const total = outputNames.length || 1;
  outputNames.forEach((o, i) => {
    outputPositions[o.name] = [1, (i + 1) / (total + 1), 1, 0];
  });

  return {
    inputs: inputNames.map((input: WidgetPort) => ({
      name: input.name,
      type: input.type,
      position: inputPositions[input.name] || 'Left',
      label: input.name,
    })),
    outputs: outputNames.map((output: WidgetPort) => ({
      name: output.name,
      type: output.type,
      position: outputPositions[output.name] || 'Right',
      label: output.name,
    })),
  };
};

export default {
  getPortsInformation,
  BarIcon: GateBarIcon,
  Element: SwitchWidget,
  hasTitle: false,
  settingsCustomSize: true,
  getWidgetTitle: (intl) => intl.formatMessage({ defaultMessage: 'Switch', id: 'LogicMaker.Widgets.Switch.Title' }),
  getGatesBarTitle: (intl) => intl.formatMessage({ defaultMessage: 'Switch', id: 'LogicMaker.Widgets.Switch.Title' }),
  CustomSettingsDialog: CustomSettingsDialog,
  getWrapperClass: () => styles.WidgetWrapper,
} as GateUI;


